import React, { useState, useRef, useEffect } from 'react'
import { X, CheckCircle, DollarSign, MessageSquare, Database, Sparkles, ListChecks } from 'lucide-react'
import { SaleWizard } from './SaleWizard'
import { HubIntroCard } from './HubIntroCard'
import { Button } from '../ui/Button'
import toast from 'react-hot-toast'
import { useHubData } from '../../hooks/useHubData'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useAuth } from '../../contexts/AuthContext'

export const DisbursementHub: React.FC<{ onClose: () => void; onSectionComplete?: () => void }> = ({ onClose, onSectionComplete }) => {
  const { user } = useAuth()
  const [isStepComplete, setIsStepComplete] = useState(false)
  const [isDisbursementHubSubmitted, setIsDisbursementHubSubmitted] = useState(() => localStorage.getItem('disbursement_hub_submitted') === 'true')
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [ticketMessage, setTicketMessage] = useState('')
  const controlsRef = useRef<any | null>(null)
  const { getData } = useHubData('acquisition')
  const access = useHubAccess('disbursement', user)
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])
  
  useEffect(() => {
    const updateCompletion = () => {
      if (!controlsRef.current?.isStepComplete) return
      const complete = controlsRef.current.isStepComplete(11)
      setIsStepComplete(complete)
    }
    
    const timeout = setTimeout(updateCompletion, 100)
    const interval = setInterval(updateCompletion, 1000)
    
    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [])

  // Listen for disbursement hub completion events
  useEffect(() => {
    const handleDisbursementHubCompleted = () => {
      setIsDisbursementHubSubmitted(true)
    }
    window.addEventListener('disbursementHubCompleted', handleDisbursementHubCompleted)
    return () => window.removeEventListener('disbursementHubCompleted', handleDisbursementHubCompleted)
  }, [])

  // Check localStorage periodically for submission status
  useEffect(() => {
    const checkSubmission = () => {
      const submitted = localStorage.getItem('disbursement_hub_submitted') === 'true'
      if (submitted !== isDisbursementHubSubmitted) {
        setIsDisbursementHubSubmitted(submitted)
      }
    }
    checkSubmission()
    const interval = setInterval(checkSubmission, 500)
    return () => clearInterval(interval)
  }, [isDisbursementHubSubmitted])

  const handleSaveProgress = () => {
    const success = controlsRef.current?.saveProgress?.()
    if (success) {
      toast.success('Progress saved successfully!')
    } else {
      toast.error('Failed to save progress. Please try again.')
    }
  }

  const handleRequestEdit = () => {
    if (!isDisbursementHubSubmitted) {
      toast.error('Please submit the Disbursement Hub section before requesting edits')
      return
    }
    setShowTicketModal(true)
  }

  const handleSubmitTicket = () => {
    if (!ticketMessage.trim()) {
      toast.error('Please enter a description of the changes needed')
      return
    }
    
    const ticketNumber = `TICKET-${Date.now().toString().slice(-6)}`
    toast.success(`Edit request submitted! Ticket #${ticketNumber}`)
    
    const tickets = JSON.parse(localStorage.getItem('edit_tickets') || '[]')
    tickets.push({
      ticketNumber,
      message: ticketMessage,
      status: 'pending',
      createdAt: new Date().toISOString(),
      section: 'Disbursement Hub'
    })
    localStorage.setItem('edit_tickets', JSON.stringify(tickets))
    
    setTicketMessage('')
    setShowTicketModal(false)
  }

  const handleComplete = () => {
    setIsStepComplete(true)
    onSectionComplete?.()
  }

  const initialData = {
    disbursementForm: getData('disbursementForm')
  }

  if (!access.canAccess) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6 my-4">
          <div className="text-center">
            <DollarSign className="h-12 w-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Disbursement Hub Locked</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {access.message || 'Please complete the Identification Hub first'}
            </p>
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-4">
        {/* Header */}
        <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <DollarSign className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Disbursement Hub</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <X className="h-5 w-5 text-secondary-600 dark:text-slate-400" />
            </button>
          </div>
          {/* Completion Progress */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-600 dark:text-slate-400">Completion</span>
              <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">{access.completionPercent}%</span>
            </div>
            <div className="h-2 bg-orange-100 dark:bg-orange-900/30 rounded-full overflow-hidden">
              <div 
                className="h-full transition-all duration-500 bg-orange-600 dark:bg-orange-400"
                style={{ width: `${access.completionPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 bg-white dark:bg-slate-800 flex flex-col">
          <HubIntroCard
            title="Disbursement Hub overview"
            subtitle="Provide clear wire and recipient details so exchange proceeds can be released accurately and on time."
            Icon={ListChecks}
            steps={[{
              title: 'Enter wire recipient details',
              description: 'Recipient name and full recipient address.',
              Icon: DollarSign
            }, {
              title: 'Add bank wiring information',
              description: 'Bank name, ABA routing number, and account number.',
              Icon: DollarSign
            }, {
              title: 'Confirm amount and review',
              description: 'Double‑check the form to avoid delays and corrections.',
              Icon: CheckCircle
            }]}
          />
          {/* Step Navigation */}
          <div className="mb-3 border-b border-slate-200 dark:border-slate-700 pb-3 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Wire Transfer Disbursement</h3>
              <span className="text-xs text-slate-500 dark:text-slate-500">
                Step 1 of 1
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              <button
                type="button"
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  isStepComplete
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                    : "bg-orange-600 text-white shadow-sm"
                }`}
              >
                <DollarSign className="h-3.5 w-3.5" />
                <span>Disbursement Form</span>
                {isStepComplete && <CheckCircle className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <SaleWizard 
              embedded 
              hideFooter 
              exposeControls={(api) => (controlsRef.current = api)} 
              mode="disbursement" 
              onClose={onClose} 
              onComplete={handleComplete}
              initialData={initialData}
            />
          </div>
        </div>

        {/* Footer - global navigation */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
          <div className="flex items-center justify-between">
            {isDisbursementHubSubmitted ? (
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300">Disbursement Hub Completed</p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    This section has been submitted and is now read-only.
                  </p>
                </div>
              </div>
            ) : (
              <Button 
                onClick={handleRequestEdit}
                disabled={true}
                variant="outline"
                className="border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500 cursor-not-allowed opacity-50"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Request Edit
              </Button>
            )}
            <div className="flex items-center space-x-3">
              {!isDisbursementHubSubmitted && (
                <Button 
                  onClick={handleSaveProgress}
                  variant="outline"
                  className="group relative border-2 border-slate-300 dark:border-slate-600 hover:border-orange-400 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <Database className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                  Save Progress
                </Button>
              )}
              <Button 
                className="group relative bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 dark:from-orange-600 dark:to-orange-500 dark:hover:from-orange-700 dark:hover:to-orange-600 text-white shadow-lg hover:shadow-xl border-0 transition-all duration-200" 
                onClick={() => {
                  console.log('Submit button clicked', { controlsRef: controlsRef.current, isDisbursementHubSubmitted })
                  try {
                    if (controlsRef.current?.submit) {
                      console.log('Calling submit function')
                      controlsRef.current.submit()
                    } else {
                      console.error('Submit function not available', controlsRef.current)
                      toast.error('Wizard not ready. Please wait a moment and try again.')
                    }
                  } catch (error) {
                    console.error('Error submitting disbursement hub:', error)
                    toast.error('Failed to submit. Please try again.')
                  }
                }}
                disabled={isDisbursementHubSubmitted}
              >
                <Sparkles className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
                {isDisbursementHubSubmitted ? 'Submitted' : 'Submit'}
              </Button>
              {isDisbursementHubSubmitted && (
                <Button 
                  onClick={handleRequestEdit}
                  variant="outline"
                  className="group relative border-2 border-[#69adba]/30 text-[#69adba] hover:border-[#69adba] hover:bg-[#69adba]/10 dark:border-[#69adba]/40 dark:text-[#69adba] dark:hover:border-[#69adba] dark:hover:bg-[#69adba]/20 hover:shadow-md transition-all duration-200"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Request Edit
                </Button>
              )}
              <Button 
                onClick={onClose} 
                variant="outline"
                className="group relative border-2 border-slate-300 dark:border-slate-600 hover:border-red-400 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <X className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform" />
                Close
              </Button>
            </div>
          </div>
        </div>

        {/* Edit Request Ticket Modal */}
        {showTicketModal && (
          <div className="modal-overlay modal-overlay-nested">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
              {/* Header */}
              <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-[#69adba]/10 to-blue-500/10 dark:from-[#69adba]/20 dark:to-blue-500/20">
                <div className="flex items-center space-x-3">
                  <MessageSquare className="h-5 w-5 text-[#69adba] dark:text-[#69adba]" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Request Edit</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowTicketModal(false)
                    setTicketMessage('')
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Please describe the changes you need to make to the Disbursement Hub:
                </p>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Describe the changes needed..."
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#69adba] dark:bg-slate-700 dark:text-slate-100 resize-none"
                  rows={6}
                />
              </div>
              
              {/* Footer */}
              <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-end space-x-3 bg-slate-50 dark:bg-slate-800/50">
                <Button
                  onClick={() => {
                    setShowTicketModal(false)
                    setTicketMessage('')
                  }}
                  variant="outline"
                  className="border-slate-300 dark:border-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitTicket}
                  disabled={!ticketMessage.trim()}
                  className="bg-gradient-to-r from-[#69adba] to-blue-500 hover:from-[#5a9aa6] hover:to-blue-600 dark:from-[#69adba] dark:to-blue-500 dark:hover:from-[#5a9aa6] dark:hover:to-blue-600 text-white border-0 shadow-lg"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

